import {getOperatorType, OperatorType} from "./OperatorType.ts";
import {getOperatorInfo, RuleEntry, RuleType} from "./RuleEntry.ts";
import {ConnectiveType, getConnectorType} from "./ConnectiveType.ts";
import {Channel} from "../channel/Channel.ts";
import {CacheManager} from "../cache/CacheManager.ts";
import useStore from "../store.ts";

export class Callback {
    private readonly callback: Function;
    private readonly channel: string;
    private readonly row: number;
    private readonly ownerId: string | null;

    constructor(callback: Function, row: number, channel: string, ownerId: string | null = null) {
        this.callback = callback;
        this.channel = channel;
        this.row = row;
        this.ownerId = ownerId;
    }

    public getCallback() {
        return this.callback;
    }

    public getChannel() {
        return this.channel;
    }

    public getRow() {
        return this.row;
    }

    public getOwnerId() {
        return this.ownerId;
    }

}

export class Rule {
    public readonly name: string;
    public value?: RuleEntry[];
    public callbacks: Callback[];
    public postfix: RuleEntry[];
    private lastHash?: string;
    private rawValue?: string;

    constructor(name: string) {
        this.name = name;
        this.callbacks = [];
        this.postfix = [];
    }

    public getName(): string {
        return this.name;
    }

    public getRawValue(): string | undefined {
        return this.rawValue;
    }

    private getEntryType(name: string): RuleType {
        if (name.startsWith('^')) return RuleType.FIELD;
        const operatorType = getOperatorType(name);
        if (operatorType != null) return RuleType.OPERATOR;
        const connectorType = getConnectorType(name);
        if (connectorType != null) return RuleType.CONNECTOR;
        return RuleType.VALUE;
    }

    private getEntryValue(key: string, type: RuleType) {
        switch (type) {
            case RuleType.OPERATOR:
                return getOperatorType(key);
            case RuleType.FIELD:
                return key.substring(1);
            case RuleType.VALUE:
                return key;
            case RuleType.CONNECTOR:
                return getConnectorType(key);
        }
    }

    public setValue(value: string, hash?: string) {
        if (hash && this.lastHash && this.lastHash === hash) {
            return;
        }

        this.value = [];
        for (const key of value.split(' ')) {
            let keyValue = key;
            if (key.startsWith("(")) {
                this.value.push(new RuleEntry(RuleType.BRACKETS_OPEN, '(', null));
                keyValue = key.substring(1);
            }
            let has_closing = false;
            if (keyValue.endsWith(")")) {
                has_closing = true;
                keyValue = keyValue.substring(0, keyValue.length - 1);
            }
            const type = this.getEntryType(keyValue);
            let entryValue = this.getEntryValue(keyValue, type);
            this.value.push(new RuleEntry(type, keyValue, entryValue));
            if (has_closing) {
                this.value.push(new RuleEntry(RuleType.BRACKETS_CLOSE, ')', null));
            }
        }
        this.value.reverse();
        this.convertRPN();
        this.rawValue = value;
        CacheManager.instance.markDirty();
        if (hash) {
            this.lastHash = hash;
        }
        // see docs/EXTRACTED_COMMENTS.md#c094
        const store = useStore();
        this.getCallbacks().forEach((c: Callback) => {
            const ownerId = c.getOwnerId();
            if (ownerId != null && !store.components[ownerId]) return;
            c.getCallback()(this, c.getRow(), c.getChannel());
        });
    }

    public getValue(): RuleEntry[] | undefined {
        return this.value;
    }

    public getCallbacks(): Callback[] {
        return this.callbacks;
    }

    public addCallback(callback: Function, row: number, channel: string, ownerId: string | null = null) {
        const exists = this.callbacks.some((existing) =>
            existing.getCallback() === callback
            && existing.getRow() === row
            && existing.getChannel() === channel
        );
        if (!exists) {
            this.callbacks.push(new Callback(callback, row, channel, ownerId));
        }
        if (this.value != undefined) {
            callback(this, row, channel);
        }
    }

    /* see docs/EXTRACTED_COMMENTS.md#c095 */
    public removeCallbacksForOwner(ownerId: string): number {
        if (!ownerId) return 0;
        const before = this.callbacks.length;
        this.callbacks = this.callbacks.filter((c) => c.getOwnerId() !== ownerId);
        return before - this.callbacks.length;
    }

    public clearCallbacks(): void {
        this.callbacks = [];
    }


    /* see docs/EXTRACTED_COMMENTS.md#c096 */
    private convertRPN() {
        if (!this.value) return;
        let tokenStack = [...this.value, new RuleEntry(RuleType.END, '\0', null)]
        let operatorStack: RuleEntry[] = []
        let outputStack: RuleEntry[] = []

        while (tokenStack.length > 0) {
            let token = tokenStack.pop();
            if (token == null) continue;
            const [precedence, associativity] = getOperatorInfo(token.getRuleType());
            switch (token.getRuleType()) {
                case RuleType.FIELD:
                case RuleType.VALUE: {
                    outputStack.push(token);
                    break;
                }
                case RuleType.BRACKETS_OPEN: {
                    operatorStack.push(token);
                    break;
                }
                case RuleType.OPERATOR:
                case RuleType.CONNECTOR: {
                    while (operatorStack.length > 0) {
                        let top = operatorStack[operatorStack.length - 1];
                        if (top.getRuleType() === RuleType.BRACKETS_OPEN) break;

                        const [topPrecedence,] = getOperatorInfo(top.getRuleType());
                        if (topPrecedence > precedence || (topPrecedence === precedence && associativity === "L")) {
                            outputStack.push(operatorStack.pop() as RuleEntry);
                        } else {
                            break;
                        }
                    }
                    operatorStack.push(token);
                    break;
                }
                case RuleType.BRACKETS_CLOSE: {
                    while (operatorStack.length > 0 && operatorStack[operatorStack.length - 1].getRuleType() !== RuleType.BRACKETS_OPEN) {
                        outputStack.push(operatorStack.pop() as RuleEntry);
                    }
                    if (operatorStack.length > 0 && operatorStack[operatorStack.length - 1].getRuleType() === RuleType.BRACKETS_OPEN) {
                        operatorStack.pop();
                    }
                    break;
                }
            }
        }
        while (operatorStack.length > 0) {
            outputStack.push(operatorStack.pop() as RuleEntry);
        }
        this.postfix = outputStack;
    }

    public async evaluate(key_channel: Channel | null, row: number): Promise<boolean> {
        return this.evaluateNow(key_channel, row);
    }

    /* see docs/EXTRACTED_COMMENTS.md#c809 */
    public evaluateNow(key_channel: Channel | null, row: number): boolean {
        let stack : Array<RuleEntry | boolean> = [...this.postfix];
        while (stack.length > 1) {
            const entry = stack.shift();
            if (entry == null) continue;
            // see docs/EXTRACTED_COMMENTS.md#c097
            if (entry === false || entry === true) {
                return <boolean>entry;
            }
            switch ((<RuleEntry>entry).getRuleType()) {
                case RuleType.FIELD:
                case RuleType.VALUE: {
                    stack.push(entry);
                    break;
                }
                case RuleType.CONNECTOR: {
                    switch ((<RuleEntry>entry).getConnectiveType()) {
                        case ConnectiveType.AND: {
                            const a = (stack.pop() as boolean)
                            const b = (stack.pop() as boolean)
                            stack.push(a && b);
                            break;
                        }
                        case ConnectiveType.OR: {
                            const a = (stack.pop() as boolean)
                            const b = (stack.pop() as boolean)
                            stack.push(a || b);
                            break;
                        }
                    }
                    break;
                }
                case RuleType.OPERATOR: {
                    switch ((<RuleEntry>entry).getOperator()) {
                        case OperatorType.EQ: {
                            const a = (stack.pop() as RuleEntry).getResult(key_channel, row);
                            const b = (stack.pop() as RuleEntry).getResult(key_channel, row);
                            stack.push(a === b);
                            break;
                        }
                        case OperatorType.EV: {
                            const a = (stack.pop() as RuleEntry).getResult(key_channel, row);
                            stack.push(a == null || a == "");
                            break;
                        }
                        case OperatorType.GE: {
                            const a = (stack.pop() as RuleEntry).getResult(key_channel, row);
                            const b = (stack.pop() as RuleEntry).getResult(key_channel, row);
                            stack.push(parseInt(`${b}`) >= parseInt(`${a}`));
                            break;
                        }
                        case OperatorType.GT: {
                            const a = (stack.pop() as RuleEntry).getResult(key_channel, row);
                            const b = (stack.pop() as RuleEntry).getResult(key_channel, row);
                            stack.push(parseInt(`${b}`) > parseInt(`${a}`));
                            break;
                        }
                        case OperatorType.LE: {
                            const a = (stack.pop() as RuleEntry).getResult(key_channel, row);
                            const b = (stack.pop() as RuleEntry).getResult(key_channel, row);
                            stack.push(parseInt(`${b}`) <= parseInt(`${a}`));
                            break;
                        }
                        case OperatorType.LT: {
                            const a = (stack.pop() as RuleEntry).getResult(key_channel, row);
                            const b = (stack.pop() as RuleEntry).getResult(key_channel, row);
                            stack.push(parseInt(`${b}`) < parseInt(`${a}`));
                            break;
                        }
                        case OperatorType.NE: {
                            const a = (stack.pop() as RuleEntry).getResult(key_channel, row);
                            const b = (stack.pop() as RuleEntry).getResult(key_channel, row);
                            stack.push(`${a}` != `${b}`);
                            break;
                        }
                        case OperatorType.NV: {
                            const a = (stack.pop() as RuleEntry).getResult(key_channel, row);
                            stack.push(a != null && a != "");
                            break;
                        }
                    }
                    break;
                }

            }
        }
        let res = stack.pop();
        return <boolean>res;
    }

}